import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vaadin/side-nav';
import '@vaadin/icon';
import '@vaadin/icons';
import '@vaadin/select';
import './theme/theme-toggle';
import { getAuth, signOut } from 'firebase/auth';
import { homeStyles } from './styles/home';
import { userService } from './_/user.service';
import { themeService, ThemeType } from './theme/theme-service';

const logoLight: any = new URL('./assets/vx.png', import.meta.url).href;
const logoDark: any  = new URL('./assets/image.png', import.meta.url).href;

@customElement('home-page')
export class HomePage extends LitElement {
  static styles = [homeStyles];

  private readonly SELECTED_SITE = 'vx-bms-selected-site';
  @state() currentTheme: ThemeType = 'light';
  @state() drawerOpen = !window.matchMedia('(max-width: 768px)').matches;
  @state() hideSiteSelector = false;
  @state() sites: any;
  @property({ type: String }) selectedSite = '';
  @property({ type: Array }) userModes: any[] = [];

  private mobileQuery = window.matchMedia('(max-width: 768px)');
  private onMobileChange = (e: MediaQueryListEvent) => {
    if (e.matches) this.drawerOpen = false;
    else this.drawerOpen = true;
  };

  async firstUpdated() {
    this.mobileQuery.addEventListener('change', this.onMobileChange);
    this.sites = userService.getSiteInfo().map((site: any) => ({
      label: site.name,
      value: String(site.id)
    }));

    const local_val = localStorage.getItem(this.SELECTED_SITE);
    this.selectedSite = local_val ? local_val : this.sites[0]['value'];
    this.handleSiteChange(this.selectedSite);

    userService.subscribe(this.onSitesChanged);
    this.userModes = userService.getModes();
  }

  connectedCallback() {
    super.connectedCallback();
    themeService.addThemeChangeListener(this.handleThemeChange);
    window.addEventListener('hide-site-selector', this.onHideSiteSelector);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    userService.unsubscribe(this.onSitesChanged);
    themeService.removeThemeChangeListener(this.handleThemeChange);
    this.mobileQuery.removeEventListener('change', this.onMobileChange);
    window.removeEventListener('hide-site-selector', this.onHideSiteSelector);
  }

  private onHideSiteSelector = (e: Event) => {
    this.hideSiteSelector = (e as CustomEvent).detail.hide;
  };

  private onSitesChanged = (newSites: any[]) => {
    this.sites = newSites;
    if (!newSites.find((site) => site.id === this.selectedSite)) {
      this.selectedSite = newSites[0]?.id ?? '';
      this.handleSiteChange(this.selectedSite);
    }
  };

  siteChangeHandler = async (event: CustomEvent) => {
    const siteId = (event.target as HTMLSelectElement).value;
    await this.handleSiteChange(siteId);
  };

  async handleSiteChange(sid: any) {
    this.selectedSite = sid;
    window.dispatchEvent(new CustomEvent('site-changed', { detail: { sid } }));
    localStorage.setItem(this.SELECTED_SITE, this.selectedSite);
  }

  handleThemeChange = (theme: ThemeType) => {
    this.currentTheme = theme;
    this.requestUpdate();
  };

  themeChange() {
    themeService.toggleTheme();
  }

  logout() {
    localStorage.removeItem(this.SELECTED_SITE);
    signOut(getAuth()).then(() => {
      window.location.href = '/login';
    });
  }

  toggleDrawer() {
    this.drawerOpen = !this.drawerOpen;
  }

  private closeOnMobile() {
    if (this.mobileQuery.matches) this.drawerOpen = false;
  }

  renderNavItem(nav: any) {
    return html`
      <vaadin-side-nav-item path="/${nav.path}" @click="${this.closeOnMobile}">
        <span class="material-icons" slot="prefix">${nav.icon}</span>
        <span class="nav-item-label">${nav.nm}</span>
      </vaadin-side-nav-item>
    `;
  }

  render() {
    return html`
      ${this.drawerOpen && this.mobileQuery.matches
        ? html`<div class="backdrop" @click="${this.toggleDrawer}"></div>`
        : ''}
      <div class="app-grid ${this.drawerOpen ? '' : 'drawer-closed'}">

        <header>
          <button class="menu-btn" @click="${this.toggleDrawer}" aria-label="Toggle menu">
            <span class="material-icons">menu</span>
          </button>
          <div class="header-actions ${this.hideSiteSelector ? 'hidden' : ''}">
            <vaadin-select
              theme="small"
              .items="${this.sites}"
              .value="${String(this.selectedSite)}"
              @change="${this.siteChangeHandler}"
              ?readonly="${!this.sites || this.sites.length === 1}"
            ></vaadin-select>
          </div>
        </header>

        <aside class="${this.drawerOpen ? '' : 'collapsed'}">
          <div class="sidebar-logo">
            <img
              class="logo"
              src="${this.currentTheme === 'dark' ? logoDark : logoLight}"
              alt="Logo"
            />
          </div>
          <div class="nav-top">
            <vaadin-side-nav>
              ${this.userModes?.map((item: any) => this.renderNavItem(item))}
            </vaadin-side-nav>
          </div>
          <div class="nav-bottom">
            <vaadin-side-nav class="nav-last" collapsible collapsed>
              <span slot="label">${userService.getUserName()}</span>
              <vaadin-side-nav-item @click="${this.themeChange}">
                <span class="material-icons" slot="suffix">
                  ${this.currentTheme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
                <span class="nav-item-label">Theme</span>
              </vaadin-side-nav-item>
              <vaadin-side-nav-item>
                <span class="material-icons" slot="suffix">logout</span>
                <span class="nav-item-label" @click="${this.logout}">Logout</span>
              </vaadin-side-nav-item>
            </vaadin-side-nav>
          </div>
        </aside>

        <main id="content-container"></main>
        <!-- <footer>
          &copy; VersionX
        </footer> -->

      </div>
    `;
  }
}
